import {
  APIRequestResult,
  RequestResultDataType,
  useFetchAPIError,
  usePrivateAPIData,
} from "contexts/api-data-provider";
import { useUser } from "contexts/UserContext";
import { maxBy } from "lodash";
import { useEffect, useMemo, useReducer, useState } from "react";
import { useDeepEqualMemo } from "utils/deepEqualMemo";
import { useAppointmentsProvider } from "./appointments-provider";
import { BatchEntryReducer } from "utils/batchEntryReducer";
import {
  appointmentsFromApiData,
  appointmentsLastUpdateTimestamp,
  appointmentsUpdated,
  selectNewer,
} from "./utils";

export function useAppointmentsById({
  appointmentIds,
  reloadTimestamp,
  onBehalfOf,
}: {
  appointmentIds: number[];
  reloadTimestamp: number | undefined;
  onBehalfOf?: string;
}): {
  appointmentsById: { [key: string]: AppointmentMetaData };
  appointmentsError?: string;
  appointmentsErrorById: { [key: string]: String };
  onBehalfOf?: string;
} {
  const { user } = useUser();
  const appointmentIdsSet = useDeepEqualMemo(new Set(appointmentIds));

  const [appointmentsByIdRequests, appointmentsByIdRequestsDispatch] =
    useReducer(BatchEntryReducer, []);

  useEffect(() => {
    appointmentsByIdRequestsDispatch({
      type: "update_required_entry_ids",
      entryIds: Array.from(appointmentIdsSet),
    });
  }, [appointmentIdsSet]);

  const appointmentsRequests = useMemo(() => {
    if (!user?.signup_form_finished) return {};
    const requests = {};
    for (const requestAppointmentIds of appointmentsByIdRequests) {
      requests[`appointmentsById_(${requestAppointmentIds.join(",")})`] = {
        method: "POST",
        resource: `/api/scheduler/v1/snapshot/appointment${
          onBehalfOf ? `?on_behalf_of=${onBehalfOf}` : ""
        }`,
        fetchArgs: {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            appointment_ids: Array.from(requestAppointmentIds),
          }),
        },
      };
    }
    return requests;
  }, [appointmentsByIdRequests, user?.signup_form_finished, onBehalfOf]);

  const appointmentsResponses = usePrivateAPIData(
    appointmentsRequests,
    reloadTimestamp,
  );

  const appointmentsResponsesById = useMemo(() => {
    return Object.values(appointmentsResponses)
      .filter((resp) => resp.result)
      .map((resp) => appointmentsFromApiData(resp.result.appointments))
      .flat();
  }, [appointmentsResponses]);

  const { appointmentUpdates, addOrUpdateAppointment } =
    useAppointmentsProvider();

  useEffect(() => {
    addOrUpdateAppointment(Object.values(appointmentsResponsesById));
  }, [appointmentsResponsesById, addOrUpdateAppointment]);

  const appointmentsError = useFetchAPIError(appointmentsResponses);

  const appointmentsById = useMemo(() => {
    const appointmentsById = {};
    for (const appointmentId of appointmentIdsSet) {
      const a1 = appointmentsResponsesById.find(
        (appt) => appt.appointment_id === appointmentId,
      );
      const a2 = appointmentUpdates[appointmentId];
      if (!a1 && !a2) continue;
      if (!a1) {
        appointmentsById[appointmentId] = a2;
      } else if (!a2) {
        appointmentsById[appointmentId] = a1;
      } else {
        appointmentsById[appointmentId] = selectNewer(a1, a2);
      }
    }
    return appointmentsById;
  }, [appointmentIdsSet, appointmentsResponsesById, appointmentUpdates]);

  const appointmentsErrorById = useMemo(() => {
    const appointmentsErrorById = {};
    for (const resp of Object.values(appointmentsResponses)) {
      if (!resp.result) continue;
      for (const appErr of resp.result.errors) {
        appointmentsErrorById[appErr.appointment_id] = appErr.error;
      }
    }
    return appointmentsErrorById;
  }, [appointmentsResponses]);

  return {
    appointmentsById,
    appointmentsError,
    appointmentsErrorById,
  };
}

export function useAppointmentsInDateRange({
  newestTimestamp,
  oldestTimestamp,
  reloadTimestamp,
  onBehalfOf,
}: {
  newestTimestamp?: number;
  oldestTimestamp?: number;
  reloadTimestamp: number | undefined;
  onBehalfOf?: string;
}): {
  appointmentsInDateRange: AppointmentMetaData[];
  appointmentsInDateRangeError?: string;
} {
  const { user } = useUser();
  const pageSize = 128;
  const [numberOfPages, setNumberOfPages] = useState(1);

  const appointmentsRequests = useMemo(() => {
    if (!user?.signup_form_finished) return {};
    const requests = {};
    for (let page = 0; page < numberOfPages; ++page) {
      const skip = page * pageSize;
      const data: Record<string, any> = {
        limit: pageSize,
        skip,
      };

      if (newestTimestamp) {
        data["newest_datetime"] = new Date(newestTimestamp).toISOString();
      }

      if (oldestTimestamp) {
        data["oldest_datetime"] = new Date(oldestTimestamp).toISOString();
      }

      if (onBehalfOf) {
        data["on_behalf_of"] = onBehalfOf;
      }

      requests[
        `appointmentsInDateRange_${page}_${newestTimestamp}_${oldestTimestamp}_${onBehalfOf}`
      ] = {
        method: "GET",
        resource: `/api/scheduler/v1/snapshot/appointments_v2?${new URLSearchParams(
          data,
        ).toString()}`,
      };
    }
    return requests;
  }, [
    newestTimestamp,
    oldestTimestamp,
    numberOfPages,
    user?.signup_form_finished,
    onBehalfOf,
  ]);

  const { appointmentUpdates, addOrUpdateAppointment } =
    useAppointmentsProvider();

  const lastUpdateTimestamp = useMemo(() => {
    return appointmentsLastUpdateTimestamp(Object.values(appointmentUpdates));
  }, [appointmentUpdates]);

  const dataReloadTimestamp =
    reloadTimestamp === undefined
      ? undefined
      : Math.max(lastUpdateTimestamp, reloadTimestamp);

  const appointmentsResponses = usePrivateAPIData(
    appointmentsRequests,
    dataReloadTimestamp,
  );

  useEffect(() => {
    const latestResp = getLatestResponse(appointmentsResponses);
    if (latestResp) {
      const d = Math.floor(latestResp.result.total_count / pageSize);
      const pages = Math.max(
        d * pageSize < latestResp.result.total_count ? d + 1 : d,
        1,
      );
      setNumberOfPages((_) => pages);
    }
  }, [appointmentsResponses]);

  const appointmentsResponsesInDateRange = useMemo<
    AppointmentMetaData[]
  >(() => {
    return Object.values(appointmentsResponses)
      .filter((resp) => resp.result)
      .map((resp) => appointmentsFromApiData(resp.result.appointments))
      .flat();
  }, [appointmentsResponses]);

  useEffect(() => {
    addOrUpdateAppointment(Object.values(appointmentsResponsesInDateRange));
  }, [appointmentsResponsesInDateRange, addOrUpdateAppointment]);

  const appointmentsInDateRange = useMemo(() => {
    const appointmentsById: { [key: string]: AppointmentMetaData } = {};
    for (const a1 of appointmentsResponsesInDateRange) {
      const a2 = appointmentUpdates[a1.appointment_id];
      if (!a2) {
        appointmentsById[a1.appointment_id] = a1;
      } else {
        appointmentsById[a1.appointment_id] = selectNewer(a1, a2);
      }
    }

    for (const appointment of Object.values(appointmentUpdates)) {
      if (
        (newestTimestamp &&
          new Date(appointment.appointment_start).getTime() >
            newestTimestamp) ||
        (oldestTimestamp &&
          new Date(appointment.appointment_end).getTime() < oldestTimestamp)
      ) {
        continue;
      }
      const a1 = appointmentsById[appointment.appointment_id];
      if (!a1) {
        appointmentsById[appointment.appointment_id] = appointment;
      }
    }

    return Object.values(appointmentsById).sort((rhs, lhs) => {
      if (lhs.appointment_start < rhs.appointment_start) return -1;
      if (lhs.appointment_start > rhs.appointment_start) return 1;
      return 0;
    });
  }, [
    appointmentUpdates,
    appointmentsResponsesInDateRange,
    newestTimestamp,
    oldestTimestamp,
  ]);

  const appointmentsInDateRangeError = useFetchAPIError(appointmentsResponses);

  return {
    appointmentsInDateRange,
    appointmentsInDateRangeError,
  };
}

export function useAppointmentsInDateRangeForMultipleUsers({
  newestTimestamp,
  oldestTimestamp,
  reloadTimestamp,
  onBehalfOf,
}: {
  newestTimestamp?: number;
  oldestTimestamp?: number;
  reloadTimestamp: number | undefined;
  onBehalfOf?: string[];
}): {
  appointmentsInDateRange: AppointmentMetaData[];
  appointmentsInDateRangeError?: string;
} {
  const { user } = useUser();
  const { appointmentUpdates, addOrUpdateAppointment } =
    useAppointmentsProvider();

  const pageSize = 128;
  const [numberOfPagesByUser, setNumberOfPagesByUser] = useState<{
    [userId: string]: number;
  }>({});

  const appointmentsRequests = useMemo(() => {
    if (!user?.signup_form_finished) return {};
    const requests = {};

    // If no onBehalfOf or empty array, query current user directly without on_behalf_of
    const userIdsToQuery = onBehalfOf && onBehalfOf.length > 0 ? onBehalfOf : [""];

    for (const userId of userIdsToQuery) {
      const userKey = userId || "current_user";
      const pagesForUser = numberOfPagesByUser[userKey] || 1;
      for (let page = 0; page < pagesForUser; ++page) {
        const skip = page * pageSize;
        const data: Record<string, any> = {
          limit: pageSize,
          skip,
        };

        if (newestTimestamp) {
          data["newest_datetime"] = new Date(newestTimestamp).toISOString();
        }

        if (oldestTimestamp) {
          data["oldest_datetime"] = new Date(oldestTimestamp).toISOString();
        }

        // Only add on_behalf_of if userId is provided (not empty string)
        if (userId) {
          data["on_behalf_of"] = userId;
        }

        requests[
          `appointmentsInDateRange_${userKey}_${page}_${newestTimestamp}_${oldestTimestamp}`
        ] = {
          method: "GET",
          resource: `/api/scheduler/v1/snapshot/appointments_v2?${new URLSearchParams(
            data,
          ).toString()}`,
        };
      }
    }
    return requests;
  }, [
    newestTimestamp,
    oldestTimestamp,
    numberOfPagesByUser,
    user?.signup_form_finished,
    onBehalfOf,
  ]);

  const lastUpdateTimestamp = useMemo(() => {
    return appointmentsLastUpdateTimestamp(Object.values(appointmentUpdates));
  }, [appointmentUpdates]);

  const dataReloadTimestamp =
    reloadTimestamp === undefined
      ? undefined
      : Math.max(lastUpdateTimestamp, reloadTimestamp);

  const appointmentsResponses = usePrivateAPIData(
    appointmentsRequests,
    dataReloadTimestamp,
  );

  useEffect(() => {
    const newNumberOfPagesByUser: { [userId: string]: number } = {};
    const userIdsToQuery = onBehalfOf && onBehalfOf.length > 0 ? onBehalfOf : [""];
    
    for (const userId of userIdsToQuery) {
      const userKey = userId || "current_user";
      const userResponses = Object.entries(appointmentsResponses).filter(
        ([key]) => key.startsWith(`appointmentsInDateRange_${userKey}_`),
      );
      const latestResp = getLatestResponse(Object.fromEntries(userResponses));
      if (latestResp) {
        const d = Math.floor(latestResp.result.total_count / pageSize);
        const pages = Math.max(
          d * pageSize < latestResp.result.total_count ? d + 1 : d,
          1,
        );
        newNumberOfPagesByUser[userKey] = pages;
      } else {
        newNumberOfPagesByUser[userKey] = numberOfPagesByUser[userKey] || 1;
      }
    }
    setNumberOfPagesByUser((prev) => {
      const hasChanged = Object.keys(newNumberOfPagesByUser).some(
        (userId) => newNumberOfPagesByUser[userId] !== prev[userId],
      );
      return hasChanged ? { ...prev, ...newNumberOfPagesByUser } : prev;
    });
  }, [appointmentsResponses, onBehalfOf, numberOfPagesByUser]);

  const appointmentsResponsesInDateRange = useMemo<
    AppointmentMetaData[]
  >(() => {
    return Object.values(appointmentsResponses)
      .filter((resp) => resp.result)
      .map((resp) => appointmentsFromApiData(resp.result.appointments))
      .flat();
  }, [appointmentsResponses]);

  useEffect(() => {
    addOrUpdateAppointment(Object.values(appointmentsResponsesInDateRange));
  }, [appointmentsResponsesInDateRange, addOrUpdateAppointment]);

  const appointmentsInDateRange = useMemo(() => {
    const appointmentsById: { [key: string]: AppointmentMetaData } = {};
    for (const a1 of appointmentsResponsesInDateRange) {
      const a2 = appointmentUpdates[a1.appointment_id];
      if (!a2) {
        appointmentsById[a1.appointment_id] = a1;
      } else {
        appointmentsById[a1.appointment_id] = selectNewer(a1, a2);
      }
    }

    for (const appointment of Object.values(appointmentUpdates)) {
      if (
        (newestTimestamp &&
          new Date(appointment.appointment_start).getTime() >
            newestTimestamp) ||
        (oldestTimestamp &&
          new Date(appointment.appointment_end).getTime() < oldestTimestamp)
      ) {
        continue;
      }
      const a1 = appointmentsById[appointment.appointment_id];
      if (!a1) {
        appointmentsById[appointment.appointment_id] = appointment;
      }
    }

    return Object.values(appointmentsById).sort((rhs, lhs) => {
      if (lhs.appointment_start < rhs.appointment_start) return -1;
      if (lhs.appointment_start > rhs.appointment_start) return 1;
      return 0;
    });
  }, [
    appointmentUpdates,
    appointmentsResponsesInDateRange,
    newestTimestamp,
    oldestTimestamp,
  ]);

  const appointmentsInDateRangeError = useFetchAPIError(appointmentsResponses);

  return {
    appointmentsInDateRange,
    appointmentsInDateRangeError,
  };
}

export function useAppointmentsByPage({
  pagesToLoad,
  pageSize,
  reloadTimestamp,
  options,
}: {
  pagesToLoad: number[];
  pageSize: number;
  reloadTimestamp: number | undefined;
  options?: {
    appointmentStatusFlags?: {
      orchidProMayHaveMissedAppointment?: boolean;
      appointmentIsCancelledOrAbandoned?: boolean;
    };
    patientUserId?: string;
  };
}): {
  appointmentTotalCount?: number;
  appointmentsByPage: { [key: string]: AppointmentMetaData[] };
  appointmentsByPageError?: string;
} {
  const { user } = useUser();
  const pagesToLoadSet = useDeepEqualMemo(new Set(pagesToLoad));

  const appointmentsRequests = useMemo(() => {
    if (!user?.signup_form_finished) return {};
    const requests = {};
    for (const page of pagesToLoadSet) {
      const skip = page * pageSize;

      const data: Record<string, any> = {
        limit: pageSize,
        skip,
      };

      if (
        options?.appointmentStatusFlags?.orchidProMayHaveMissedAppointment !==
        undefined
      ) {
        data["orchid_pro_may_have_missed_appointment"] =
          options.appointmentStatusFlags.orchidProMayHaveMissedAppointment;
      }

      if (
        options?.appointmentStatusFlags?.appointmentIsCancelledOrAbandoned !==
        undefined
      ) {
        data["appointment_is_cancelled_or_abandoned"] =
          options.appointmentStatusFlags.appointmentIsCancelledOrAbandoned;
      }

      if (options?.patientUserId) {
        data["patient_user_id"] = options.patientUserId;
      }

      requests[
        `appointmentsByPage_${pageSize}_${page}_${JSON.stringify(
          options?.appointmentStatusFlags,
        )}`
      ] = {
        method: "GET",
        resource: `/api/scheduler/v1/snapshot/appointments_v2?${new URLSearchParams(
          data,
        ).toString()}`,
      };
    }
    return requests;
  }, [
    user?.signup_form_finished,
    pagesToLoadSet,
    pageSize,
    options?.appointmentStatusFlags,
    options?.patientUserId,
  ]);

  const { appointmentUpdates, addOrUpdateAppointment } =
    useAppointmentsProvider();

  const lastUpdateTimestamp = useMemo(() => {
    return appointmentsLastUpdateTimestamp(Object.values(appointmentUpdates));
  }, [appointmentUpdates]);

  const dataReloadTimestamp =
    reloadTimestamp === undefined
      ? undefined
      : Math.max(lastUpdateTimestamp, reloadTimestamp);

  const appointmentsResponses = usePrivateAPIData(
    appointmentsRequests,
    dataReloadTimestamp,
  );

  const appointmentsResponsesByPage = useMemo(() => {
    const pages: { [key: string]: AppointmentMetaData[] } = {};
    for (const [requestId, response] of Object.entries(appointmentsResponses)) {
      if (!response.result) continue;
      const page = requestId.split("_")[2];
      pages[page] = appointmentsFromApiData(response.result.appointments);
    }
    return pages;
  }, [appointmentsResponses]);

  useEffect(() => {
    addOrUpdateAppointment(Object.values(appointmentsResponsesByPage).flat());
  }, [appointmentsResponsesByPage, addOrUpdateAppointment]);

  const appointmentsByPage = useMemo(() => {
    const appointmentsByPage = {};
    for (const [page, appointments] of Object.entries(
      appointmentsResponsesByPage,
    )) {
      appointmentsByPage[page] = appointmentsUpdated(
        appointments,
        appointmentUpdates,
      );
    }
    return appointmentsByPage;
  }, [appointmentUpdates, appointmentsResponsesByPage]);

  const appointmentTotalCount = useMemo(
    () => getLatestResponse(appointmentsResponses)?.result?.total_count,
    [appointmentsResponses],
  );

  const appointmentsByPageError = useFetchAPIError(appointmentsResponses);

  return {
    appointmentTotalCount,
    appointmentsByPage,
    appointmentsByPageError,
  };
}

function getLatestResponse(
  results: APIRequestResult,
): undefined | RequestResultDataType {
  return maxBy(
    Object.values(results).filter((res) => !!res.result),
    (res) => res.result.last_modify_datetime,
  );
}
